import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { WifiOff, Loader2 } from 'lucide-react';
import { cn } from '../../ui/utils';

/**
 * Blocking, non-dismissible overlay shown when a write/timer sync fails offline.
 * Uses a Radix modal dialog so focus is trapped and the page underneath is inert.
 */
const OfflineModal = ({ isOpen }) => {
  React.useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  return (
    <DialogPrimitive.Root
      open={isOpen}
      modal
      onOpenChange={() => {
        // Controlled + non-dismissible — ignore close attempts
      }}
    >
      <DialogPrimitive.Portal>
        {/* Full-viewport capture layer above all assignment UI */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[200] bg-slate-900/55 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
          onContextMenu={(event) => event.preventDefault()}
        />
        <DialogPrimitive.Content
          aria-modal="true"
          aria-describedby="offline-modal-description"
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onFocusOutside={(event) => event.preventDefault()}
          className={cn(
            'fixed left-1/2 top-1/2 z-[201] w-[calc(100%-2rem)] max-w-md',
            '-translate-x-1/2 -translate-y-1/2 outline-none',
            'rounded-2xl border border-amber-200/80 bg-white p-8 shadow-xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
          )}
        >
          <div className="flex flex-col items-center text-center">
            <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-amber-200 bg-amber-50">
              <WifiOff className="h-8 w-8 text-amber-600" aria-hidden />
            </div>

            <DialogPrimitive.Title className="text-xl font-semibold tracking-tight text-foreground">
              Connection Lost
            </DialogPrimitive.Title>

            <DialogPrimitive.Description
              id="offline-modal-description"
              className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground"
            >
              Your test has been paused due to internet connection issues. It will resume
              automatically when your connection is restored.
            </DialogPrimitive.Description>

            <div
              className="mt-6 flex w-full flex-col items-center gap-3 rounded-xl border border-border/60 bg-muted/40 px-4 py-4"
              role="status"
              aria-live="polite"
            >
              <Loader2 className="h-6 w-6 animate-spin text-amber-600" aria-hidden />
              <p className="text-sm font-medium text-foreground">Waiting for connection…</p>
              <p className="text-xs text-muted-foreground">
                Please check your internet and stay on this page.
              </p>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export { OfflineModal };
